/* eslint-disable camelcase */
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Skeleton,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState } from 'react';

import { Bank } from '@finley-technologies/finley-types';
import {
  ValleyBankLoanAccountWithCollateralAndStats,
  orderLoanAccountsLoans,
  getZeroOrRounded,
} from '@finley-technologies/valley-bank-utils';

import {
  useAddToValleyBankAssignmentQueueMutation,
  useGetUserQuery,
  useGetValleyBankAssignmentQueueQuery,
  ValleyBankAddToAssignmentQueueGQLType,
} from '../../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { AssignedCollateralAccounts } from './AssignedCollateralAccounts';
import { getCustomerFromReceivable } from './shared';
import {
  getUnexpectedErrorMessage,
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from 'packages/common-frontend/utils';
import { Alert } from '~/packages/common-frontend/components/Alert';
import { CsvXlsxDownloadSelect } from '~/packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  errorToastConfig,
  errorToastId,
  successToastConfig,
} from '~/packages/common-frontend/constants';
import { InfoCard } from '~/valleyBank/domains/components/InfoCard';
import { CustomerSummaryLink } from '~/valleyBank/domains/Customers/hooks/CustomerContext';
import { LoansTable } from '~/valleyBank/domains/LoanAccounts/ui/LoansTable';

const loanPartiesDataKeyToHeaderMap = {
  cif_num: 'CIF Number',
  full_name: 'Customer',
  rel_code_description: 'Customer Type',
};

type UnassignModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onUnassign: () => void;
  collateralAccountNum: string;
  loanAccountNum: string;
};
const UnassignModal = ({
  isOpen,
  onCancel,
  onUnassign,
  collateralAccountNum,
  loanAccountNum,
}: UnassignModalProps) => {
  const cancelRef = React.useRef(null);
  const buttonColorScheme = useColorModeValue('aurora', 'gray');
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onCancel}
      size="xl"
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Confirm Unassignment
          </AlertDialogHeader>

          <AlertDialogBody>
            <>
              <Text
                mb={2}
              >{`Are you sure you want to unassign ${collateralAccountNum} from ${loanAccountNum}?`}</Text>
              <Box py="12px" alignSelf="flex-end">
                <HStack spacing="25px">
                  <Spacer />
                  <Button
                    variant="outline"
                    colorScheme={buttonColorScheme}
                    onClick={() => onCancel()}
                    px="20px"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="solid"
                    colorScheme={buttonColorScheme}
                    onClick={() => {
                      onUnassign();
                    }}
                    px="20px"
                  >
                    Unassign
                  </Button>
                </HStack>
              </Box>
            </>
          </AlertDialogBody>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

// @jendevelops TODO: remove optional prop when fetching in component
type LoanSummaryProps = {
  loanAccount: ValleyBankLoanAccountWithCollateralAndStats;
  canUnassign?: boolean;
  queuedSelectedCollateralAccounts: Set<string> | null;
  toggleCollateralSelect: (params: {
    collateralAccountNumber: string;
    isChecked: boolean;
  }) => void;
  isLoading: boolean;
  isError: boolean;
};
export const LoanSummary = ({
  loanAccount,
  canUnassign,
  queuedSelectedCollateralAccounts,
  toggleCollateralSelect,
  isLoading,
  isError,
}: LoanSummaryProps) => {
  const toast = useToast();

  const { data: userData } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  const {
    data: assignmentQueueData,
    refetch: refetchAssignmentQueue,
  } = useGetValleyBankAssignmentQueueQuery({
    fetchPolicy: 'network-only',
  });

  const [
    addToValleyBankAssignmentQueueMutation,
  ] = useAddToValleyBankAssignmentQueueMutation({
    fetchPolicy: 'network-only',
    onCompleted(data) {
      // mutation returns back a list of errors
      // surface any errors here
      if (data.valleyBankAddToAssignmentsQueue.length > 0) {
        if (!toast.isActive(errorToastId)) {
          toast({
            ...errorToastConfig,
            title: 'Errors on Unassignment',
            description: data.valleyBankAddToAssignmentsQueue.join('; '),
          });
        }
      } else {
        toast({
          ...successToastConfig,
          title: 'Successfully add unassignment to queue.',
        });
      }
    },
  });

  const [
    unassignment,
    setUnassignment,
  ] = useState<ValleyBankAddToAssignmentQueueGQLType>();

  if (!userData || !userData.user || !assignmentQueueData) {
    return <Skeleton width="full" />;
  }

  const {
    loanAccountNumber,
    principalBalance,
    totalMarketValue,
    effectiveAllocationRate,
    totalPostAllocationLendableValue,
    effectiveAdvanceRate,
    totalLendableValue,
    marginAvailable,
    marginAvailabilityRate,
    collateralAccounts,
    loanParties,
  } = loanAccount;

  const userId = userData.user.id;

  const sortedLoanParties = loanParties.sort((a, b) => {
    if (a.rel_code_description === null) return 1; // move `a` to end
    if (b.rel_code_description === null) return -1; // move `b` to end
    return a.rel_code_description < b.rel_code_description ? -1 : 1;
  });

  const loanPartiesHeaders = _.map(
    loanPartiesDataKeyToHeaderMap,
    (header, key) => ({ label: header, key }),
  );

  const sortedCollateralAccounts = collateralAccounts.sort((a, b) => {
    const customerA = getCustomerFromReceivable(a.receivables, loanParties);
    const customerB = getCustomerFromReceivable(b.receivables, loanParties);

    if (customerA && customerB) {
      if (customerA.rel_code === null) return 1; // move to end
      if (customerB.rel_code === null) return -1; // move to end
      return customerA.rel_code < customerB.rel_code ? -1 : 1;
    }
    if (customerA) {
      return -1;
    }
    return 1;
  });

  const { roundedValue: roundedMarginAvailabilityRateValue } = getZeroOrRounded(
    marginAvailabilityRate * 100,
  );

  return (
    <VStack w="100%" spacing="24px" alignItems="flex-start">
      <Flex gridRowGap="24px" gridColumnGap="24px" w="100%" flexWrap="wrap">
        <InfoCard
          title="Outstanding Balance"
          value={formatCurrencyAmount(principalBalance, Bank.Currency.USD)}
          icon={<Icon icon="dollar" size="24px" />}
          w="400px"
        />
        <InfoCard
          title="Collateral Market Value"
          value={formatCurrencyAmount(totalMarketValue, Bank.Currency.USD)}
          icon={<Icon icon="dollar" size="24px" />}
          w="400px"
        />
        <InfoCard
          title="Collateral Blended Advance Rate"
          value={percentageToPercentageLabel(effectiveAdvanceRate * 100, 2)}
          icon={<Icon icon="percentage" size="24px" />}
          w="400px"
        />
        <InfoCard
          title="Collateral Pre Allocation Lending Value"
          value={formatCurrencyAmount(totalLendableValue, Bank.Currency.USD)}
          icon={<Icon icon="dollar" size="24px" />}
          w="400px"
        />
        <InfoCard
          title="Collateral Blended Allocation"
          value={percentageToPercentageLabel(effectiveAllocationRate * 100, 2)}
          icon={<Icon icon="percentage" size="24px" />}
          w="400px"
        />
        <InfoCard
          title="Collateral Post Allocation Lending Value"
          value={formatCurrencyAmount(
            totalPostAllocationLendableValue,
            Bank.Currency.USD,
          )}
          icon={<Icon icon="dollar" size="24px" />}
          w="400px"
        />
        <InfoCard
          title="Margin Availability"
          value={formatCurrencyAmount(marginAvailable, Bank.Currency.USD)}
          icon={<Icon icon="dollar" size="24px" />}
          w="400px"
        />
        <InfoCard
          title="Margin Availability Rate"
          value={percentageToPercentageLabel(
            roundedMarginAvailabilityRateValue,
            2,
          )}
          icon={<Icon icon="percentage" size="24px" />}
          w="400px"
        />
      </Flex>

      <Flex alignItems="center" mt="64px" w="100%">
        <Heading as="h2" fontSize="24px">
          Loan Parties
        </Heading>
        <Spacer />
        {loanParties.length > 0 && (
          <CsvXlsxDownloadSelect
            dataRows={sortedLoanParties}
            headers={loanPartiesHeaders}
            filenamePrefix={`loan-account-${loanAccountNumber}-loan-parties`}
          />
        )}
      </Flex>
      {loanParties.length === 0 && (
        <Alert status="info">
          <Text>No customers are associated with this loan account</Text>
        </Alert>
      )}
      {loanParties.length > 0 && (
        <Table>
          <Thead>
            <Tr>
              {_.map(loanPartiesDataKeyToHeaderMap, (header) => (
                <Th key={header}>{header}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {sortedLoanParties.map(
              ({ cif_num, rel_code_description, full_name }) => (
                <Tr key={cif_num}>
                  <Td>
                    <CustomerSummaryLink cifnum={cif_num}>
                      {cif_num}
                    </CustomerSummaryLink>
                  </Td>
                  <Td>
                    <CustomerSummaryLink cifnum={cif_num}>
                      {full_name}
                    </CustomerSummaryLink>
                  </Td>
                  <Td>{rel_code_description}</Td>
                </Tr>
              ),
            )}
          </Tbody>
        </Table>
      )}
      <AssignedCollateralAccounts
        assignmentQueueData={assignmentQueueData}
        queuedSelectedCollateralAccounts={queuedSelectedCollateralAccounts}
        toggleCollateralSelect={toggleCollateralSelect}
        loanAccountNumber={loanAccountNumber}
        canUnassign={Boolean(canUnassign)}
        collateralAccounts={sortedCollateralAccounts}
        loanParties={loanParties}
        isLoading={isLoading}
        isError={isError}
        setUnassignment={setUnassignment}
        userId={userId}
      />
      {unassignment !== undefined && (
        <UnassignModal
          isOpen={!!unassignment}
          onCancel={() => {
            setUnassignment(undefined);
          }}
          onUnassign={async () => {
            await addToValleyBankAssignmentQueueMutation({
              variables: {
                records: [unassignment],
              },
            });
            await refetchAssignmentQueue();
            setUnassignment(undefined);
          }}
          collateralAccountNum={unassignment.collateralAccountNumber}
          loanAccountNum={unassignment.loanAccountNumber}
        />
      )}
      {/* @jenDevelops use getLoans when piped in from other PR */}
      <Box mt="64px" w="100%">
        <LoansTable
          title="Loan Summary"
          noLoansMessage="There are no loans associated with this loan account."
          loans={orderLoanAccountsLoans([loanAccount])}
          showStats={false}
          isLoading={isLoading}
          isError={isError}
          downloadFilenamePrefix={`loans-loan-account-${loanAccountNumber}`}
          pageType="loan-accounts"
        />
      </Box>
    </VStack>
  );
};
